import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { newsAPI, uploadAPI } from '../../lib/api';
import { formatDate } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { RichTextEditor } from '../ui/rich-text-editor';
import { toast } from 'sonner';
import { SITE_NAMES } from '../../lib/api';
import { 
  Plus, ArrowLeft, Pencil, Trash2, Eye, EyeOff, 
  Calendar, Search, MoreVertical, Upload
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

export const NewsListAdmin = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    loadNews();
  }, []);

  const loadNews = async () => {
    try {
      const response = await newsAPI.getAll(null, false);
      setNews(response.data);
    } catch (error) {
      toast.error('Nepodařilo se načíst aktuality');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await newsAPI.delete(deleteId);
      setNews(news.filter(n => n.id !== deleteId));
      toast.success('Aktualita byla smazána');
    } catch (error) {
      toast.error('Nepodařilo se smazat aktualitu');
    } finally {
      setDeleteId(null);
    }
  };

  const filteredNews = news.filter(n => 
    n.title.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">Aktuality</h1>
          <p className="text-[#64748B]">Správa aktualit a novinek</p>
        </div>
        <Link to="/admin/aktuality/nova">
          <Button className="rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]" data-testid="add-news">
            <Plus size={18} className="mr-2" />
            Nová aktualita
          </Button>
        </Link>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" size={18} />
        <Input
          placeholder="Hledat aktuality..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10 rounded-xl"
          data-testid="search-news"
        />
      </div>

      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>Název</th>
              <th className="hidden md:table-cell">Datum</th>
              <th className="hidden md:table-cell">Autor</th>
              <th className="hidden lg:table-cell">Sekce</th>
              <th>Stav</th>
              <th className="w-20">Akce</th>
            </tr>
          </thead>
          <tbody>
            {filteredNews.length > 0 ? (
              filteredNews.map((item) => (
                <tr key={item.id} data-testid={`news-row-${item.id}`}>
                  <td>
                    <div className="font-medium text-[#2C3E50]">{item.title}</div>
                    <div className="text-xs text-[#64748B] md:hidden">{formatDate(item.created_at)}</div>
                  </td>
                  <td className="hidden md:table-cell text-[#64748B]">{formatDate(item.created_at)}</td>
                  <td className="hidden md:table-cell text-[#64748B]">{item.author_name}</td>
                  <td className="hidden lg:table-cell">
                    <span className={`badge ${item.site === 'ds' ? 'badge-warning' : 'badge-info'}`}>
                      {SITE_NAMES[item.site] || 'MŠ'}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${item.is_published ? 'badge-success' : 'badge-warning'}`}>
                      {item.is_published ? 'Publikováno' : 'Koncept'}
                    </span>
                  </td>
                  <td>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical size={16} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem asChild>
                          <Link to={`/admin/aktuality/${item.id}`} className="cursor-pointer">
                            <Pencil size={14} className="mr-2" />
                            Upravit
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to={`/aktuality/${item.id}`} target="_blank" className="cursor-pointer">
                            <Eye size={14} className="mr-2" />
                            Zobrazit
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => setDeleteId(item.id)}
                          className="cursor-pointer text-red-600"
                        >
                          <Trash2 size={14} className="mr-2" />
                          Smazat
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="text-center py-8 text-[#64748B]">
                  {search ? 'Žádné výsledky' : 'Žádné aktuality'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Smazat aktualitu?</AlertDialogTitle>
            <AlertDialogDescription>
              Tato akce je nevratná. Aktualita bude trvale smazána.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Zrušit</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Smazat
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export const NewsFormAdmin = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    excerpt: '',
    image_url: '',
    is_published: false,
    publish_date: '',
    site: 'ms'
  });

  useEffect(() => {
    if (isEdit) {
      loadNews();
    }
  }, [id]);

  const loadNews = async () => {
    try {
      const response = await newsAPI.getOne(id);
      setFormData(response.data);
    } catch (error) {
      toast.error('Nepodařilo se načíst aktualitu');
      navigate('/admin/aktuality');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEdit) {
        await newsAPI.update(id, formData);
        toast.success('Aktualita byla aktualizována');
      } else {
        await newsAPI.create(formData);
        toast.success('Aktualita byla vytvořena');
      }
      navigate('/admin/aktuality');
    } catch (error) {
      toast.error('Nepodařilo se uložit aktualitu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div>;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/admin/aktuality" className="text-[#64748B] hover:text-[#4ECDC4]">
          <ArrowLeft size={24} />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-[#2C3E50]">
            {isEdit ? 'Upravit aktualitu' : 'Nová aktualita'}
          </h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-100 p-6 space-y-6">
        <div className="space-y-2">
          <Label htmlFor="title">Název *</Label>
          <Input
            id="title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            className="rounded-xl"
            data-testid="news-title-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="excerpt">Krátký popis</Label>
          <Input
            id="excerpt"
            name="excerpt"
            value={formData.excerpt || ''}
            onChange={handleChange}
            className="rounded-xl"
            placeholder="Krátký úvod pro náhled"
            data-testid="news-excerpt-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="content">Obsah *</Label>
          <RichTextEditor
            value={formData.content}
            onChange={(html) => setFormData(prev => ({ ...prev, content: html }))}
            testId="news-content-input"
          />
        </div>

        <div className="space-y-2">
          <Label>Obrázek aktuality</Label>
          <div className="flex gap-2 items-center">
            {formData.image_url && (
              <img src={formData.image_url} alt="Preview" className="w-20 h-20 rounded-xl object-cover flex-shrink-0" />
            )}
            <label className="cursor-pointer flex-1">
              <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setUploading(true);
                try {
                  const res = await uploadAPI.upload(file);
                  setFormData(prev => ({ ...prev, image_url: res.data.url }));
                } catch (err) { console.error(err); }
                finally { setUploading(false); }
              }} />
              <Button type="button" variant="outline" className="w-full rounded-xl" disabled={uploading} asChild>
                <span><Upload size={16} className="mr-2" />{uploading ? 'Nahrávání...' : formData.image_url ? 'Zmenit obrázek' : 'Nahrát obrázek'}</span>
              </Button>
            </label>
            {formData.image_url && (
              <Button type="button" variant="ghost" size="sm" className="text-red-500" onClick={() => setFormData(prev => ({ ...prev, image_url: '' }))}>
                <Trash2 size={16} />
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Label>Sekce</Label>
          <Select value={formData.site || 'ms'} onValueChange={(value) => setFormData(prev => ({ ...prev, site: value }))}>
            <SelectTrigger className="rounded-xl" data-testid="news-site-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ms">{SITE_NAMES.ms}</SelectItem>
              <SelectItem value="ds">{SITE_NAMES.ds}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
          <div>
            <div className="font-medium text-[#2C3E50]">Publikovat</div>
            <div className="text-sm text-[#64748B]">Zobrazit na webu</div>
          </div>
          <Switch
            checked={formData.is_published}
            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_published: checked }))}
            data-testid="news-publish-switch"
          />
        </div>

        <div className="flex gap-4 pt-4">
          <Button
            type="submit"
            disabled={saving}
            className="flex-1 rounded-full bg-[#4ECDC4] hover:bg-[#3DBDB5]"
            data-testid="news-save-btn"
          >
            {saving ? 'Ukládání...' : (isEdit ? 'Uložit změny' : 'Vytvořit aktualitu')}
          </Button>
          <Link to="/admin/aktuality" className="flex-1">
            <Button type="button" variant="outline" className="w-full rounded-full">
              Zrušit
            </Button>
          </Link>
        </div>
      </form>
    </div>
  );
};

export default NewsListAdmin;
